import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import type { BusinessRole } from '@/lib/supabase/database.types';
import { getInitials, mapBusinessRole, type UserRole } from '@/domain/auth/access';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  businessRole?: BusinessRole;
  initials: string;
  businessId?: string;
  businessName?: string;
  businessType?: string;
  isB2B: boolean;
  membershipId?: string;
  activeModules: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

async function resolveAuthUser(session: Session): Promise<AuthUser> {
  const authUser = session.user;
  const email = authUser.email ?? '';

  const [{ data: profile, error: profileError }, { data: platformAdmin, error: adminError }] =
    await Promise.all([
      supabase.from('profiles').select('full_name').eq('id', authUser.id).maybeSingle(),
      supabase.from('platform_admins').select('user_id').eq('user_id', authUser.id).maybeSingle(),
    ]);

  if (profileError) throw profileError;
  if (adminError) throw adminError;

  const name =
    profile?.full_name ||
    (typeof authUser.user_metadata.full_name === 'string' ? authUser.user_metadata.full_name : '') ||
    email.split('@')[0] ||
    'Usuario';

  if (platformAdmin) {
    return {
      id: authUser.id,
      name,
      email,
      role: 'super_admin',
      initials: getInitials(name),
      isB2B: false,
      activeModules: ['orders', 'history', 'customers', 'products', 'kitchen', 'whatsapp_ai', 'reports', 'inventory'],
    };
  }

  const { data: membership, error: membershipError } = await supabase
    .from('business_memberships')
    .select('id,business_id,role,permissions')
    .eq('user_id', authUser.id)
    .eq('status', 'active')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (membershipError) throw membershipError;
  if (!membership) {
    throw new Error('Tu cuenta todavía no tiene acceso activo a un negocio de FoodDash.');
  }

  const [businessResult, modulesResult] = await Promise.all([
    supabase.from('businesses').select('id,name,business_type').eq('id', membership.business_id).single(),
    supabase
      .from('business_modules')
      .select('module_key')
      .eq('business_id', membership.business_id)
      .eq('enabled', true),
  ]);

  const { data: business, error: businessError } = businessResult;
  const { data: modules, error: modulesError } = modulesResult;

  if (businessError) throw businessError;
  if (modulesError) throw modulesError;

  const businessModuleKeys = modules.map((module) => module.module_key);
  const metadataSellerPermissions = Array.isArray(authUser.user_metadata.seller_permissions)
    ? authUser.user_metadata.seller_permissions.filter((permission): permission is string => typeof permission === 'string')
    : null;
  const storedSellerPermissions = membership.permissions ?? metadataSellerPermissions;
  const activeModules = membership.role === 'employee' && storedSellerPermissions
    ? businessModuleKeys.filter((moduleKey) => storedSellerPermissions.includes(moduleKey))
    : businessModuleKeys;

  return {
    id: authUser.id,
    name,
    email,
    role: mapBusinessRole(membership.role),
    businessRole: membership.role,
    initials: getInitials(name),
    businessId: business.id,
    businessName: business.name,
    businessType: business.business_type ?? undefined,
    isB2B: /b2b|food\s*service|mayorista|distribuidor/i.test(business.business_type ?? '') || modules.some((module) => module.module_key.startsWith('b2b_')),
    membershipId: membership.id,
    activeModules,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hydrateSession = useCallback(async (session: Session | null) => {
    if (!session) {
      setUser(null);
      setError(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    try {
      const resolvedUser = await resolveAuthUser(session);
      setUser(resolvedUser);
      setError(null);
      return resolvedUser;
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'No fue posible cargar tu acceso.';
      setUser(null);
      setError(message);
      throw cause;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      void hydrateSession(data.session).catch(() => undefined);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      void hydrateSession(session).catch(() => undefined);
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [hydrateSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) throw signInError;
      if (!data.session) throw new Error('Supabase no devolvió una sesión válida.');

      try {
        const resolvedUser = await hydrateSession(data.session);
        if (!resolvedUser) throw new Error('No fue posible resolver el usuario autenticado.');
        return resolvedUser;
      } catch (cause) {
        await supabase.auth.signOut();
        throw cause;
      }
    },
    [hydrateSession],
  );

  const logout = useCallback(async () => {
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) throw signOutError;
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: Boolean(user), loading, error, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe utilizarse dentro de AuthProvider.');
  return context;
}
